'use client';

import { useMemo } from 'react';
import { useEffectivePermissions } from '@/lib/iam/useEffectivePermissions';
import { useActiveSolutionKey } from '@/lib/operations/activeSolutionKeyStore';
import {
  getEffectiveOperationsActionPermission,
  overlaySolutionPermissions,
  OPERATIONS_ACTION_KEYS,
  type OperationsActionKey,
} from '@/lib/operations/actionPermissions';
import type { SmartTaskModalAction } from '@/config/industry-solutions/types';
import { isTourVideoCaptureMode } from '@/lib/tourCaptureMode';

export interface WorkflowActionPermissionState {
  settings: Record<string, unknown> | null;
  role: string;
  isLoading: boolean;
  /** Call to invalidate the cache and re-fetch effective permissions. */
  refresh: () => void;
}

/**
 * Hook to load the current user's operations feature settings and role.
 *
 * IAM Phase 1: Now delegates to `useEffectivePermissions()` which calls the
 * unified `/api/v1/iam/effective-permissions` endpoint. This is the single
 * resolution path — same source as `configResolver` / `SmartTaskShell`.
 * The `settings` field is the flat `permissions` object (snake_case keys),
 * preserving backward compatibility with `isWorkflowActionPermitted`.
 */
export function useWorkflowActionPermissions(): WorkflowActionPermissionState {
  const effective = useEffectivePermissions('total-operations');
  // The active industry solution's slice replaces the flat merge for its own
  // surface — a shared workflow (fleet-management under two solutions)
  // resolves its feature flags and action matrix from the active solution's
  // groups only (nothing competes across industry solutions).
  const activeSolutionKey = useActiveSolutionKey();

  return useMemo<WorkflowActionPermissionState>(() => {
    if (effective.isLoading) {
      return { settings: null, role: 'user', isLoading: true, refresh: effective.refresh };
    }

    return {
      // Always pass permissions through — the Actions panel in
      // PermissionsSlideOver is the sole source of truth for what a user
      // can do. The old hasAccess gate caused settings to be null when
      // module_access was missing, which made all item types invisible.
      settings: overlaySolutionPermissions(
        effective.permissions,
        activeSolutionKey ? effective.solutionPermissions?.[activeSolutionKey] : null,
        { overridesOnly: effective.overridesOnly },
      ),
      role: effective.role,
      isLoading: false,
      refresh: effective.refresh,
    };
  }, [effective.isLoading, effective.permissions, effective.solutionPermissions, effective.overridesOnly, effective.role, effective.refresh, activeSolutionKey]);
}

/**
 * Maps non-standard verb prefixes found in `requires_permission` YAML keys to
 * the corresponding `OperationsActionKey` used by the RBAC system.
 *
 * This bridges the gap between workflow-specific permission names (e.g.
 * `start_waste_collection`, `perform_qc_inspection`) and the standard RBAC
 * action keys (`create`, `resolve`, `close`, etc.) that are toggled in
 * `ActionPermissionsTable.tsx`.
 */
const ACTION_VERB_SYNONYMS: Record<string, OperationsActionKey> = {
  start: 'create',
  log: 'create',
  deploy: 'create',
  raise: 'create',
  conduct: 'create',
  register: 'create',
  add: 'create',
  escalate: 'create',
  perform: 'resolve',
  complete: 'resolve',
  approve: 'resolve',
  view: 'resolve',
  track: 'resolve',
  scan: 'resolve',
  manage: 'create',
};

/**
 * Determine whether a workflow modal itself is accessible for the current user.
 *
 * Checks the modal-level `rbac_permission` field against the user's feature
 * settings. If an explicit boolean is set for the key, it is respected.
 * If no setting exists, access is DENIED by default — the RBAC table must
 * explicitly grant the permission. This prevents users from gaining access
 * merely because a permission key is not recognized or configured.
 *
 * Exception: admin/owner/root roles are allowed by default since they have
 * unrestricted access.
 */
export function isWorkflowModalAccessPermitted(
  rbacPermission: string | undefined,
  settings: Record<string, unknown> | null,
  role?: string,
): boolean {
  if (!rbacPermission) return true;
  if (isTourVideoCaptureMode()) return true;

  const featureValue = settings?.[rbacPermission];
  if (typeof featureValue === 'boolean') {
    return featureValue;
  }

  // Deny by default — permission must be explicitly granted
  return false;
}

/**
 * Determine whether a workflow modal action is permitted for the current user.
 *
 * Uses the same RBAC source as the rest of total-operations:
 * `operations_action_permissions` from permissionDefinitions.ts. Supports
 * all OperationsActionKey prefixes: create_*, resolve_*, close_*, admin_reopen_*,
 * reopen_*, delete_*, print_*. Also maps non-standard verb prefixes (start_*,
 * log_*, perform_*, etc.) to the closest RBAC action via synonym mapping.
 * Falls back to explicit team feature toggles for non-action keys
 * (e.g. `smart_task_appointments`).
 *
 * Unknown/unconfigured permission keys are DENIED by default. This prevents
 * users from gaining access merely because a permission key is not recognized
 * or configured. Admin/owner/root roles bypass this default.
 */
export function isWorkflowActionPermitted(
  action: Pick<SmartTaskModalAction, 'requires_permission'>,
  settings: Record<string, unknown> | null,
  role: string,
  workflowKey?: string | null,
): boolean {
  const requiresPermission = action.requires_permission;
  if (!requiresPermission) {
    return true;
  }
  if (isTourVideoCaptureMode()) return true;

  // Format: "item_type.action_key" (e.g. "stock_adjustment.issue_to_van")
  // This is the preferred format for workflow-specific action permissions
  // that are managed via the Actions tab in PermissionsSlideOver.
  const dotMatch = requiresPermission.match(/^([a-z_]+)\.([a-z_]+)$/);
  if (dotMatch) {
    const itemType = dotMatch[1];
    const actionKey = dotMatch[2] as OperationsActionKey;
    if (OPERATIONS_ACTION_KEYS.includes(actionKey)) {
      return getEffectiveOperationsActionPermission(settings, role, itemType, actionKey, workflowKey);
    }
  }

  // Handle operations action permissions first: create_appointment, resolve_appointment, etc.
  const actionMatch = requiresPermission.match(/^(create|resolve|close|admin_reopen|reopen|delete|print)_(.+)$/);
  if (actionMatch) {
    const actionKey = actionMatch[1] as OperationsActionKey;
    const itemType = actionMatch[2];
    return getEffectiveOperationsActionPermission(settings, role, itemType, actionKey, workflowKey);
  }

  // Try synonym mapping: start_waste_collection → create + waste_collection
  const synonymMatch = requiresPermission.match(/^([a-z]+)_(.+)$/);
  if (synonymMatch) {
    const verb = synonymMatch[1];
    const itemType = synonymMatch[2];
    const mappedAction = ACTION_VERB_SYNONYMS[verb];
    if (mappedAction) {
      return getEffectiveOperationsActionPermission(settings, role, itemType, mappedAction, workflowKey);
    }
  }

  // If team feature settings contain an explicit boolean for this permission key,
  // respect it directly (e.g. `smart_task_appointments` from permissionDefinitions.ts).
  const featureValue = settings?.[requiresPermission];
  if (typeof featureValue === 'boolean') {
    return featureValue;
  }

  // Deny by default — permission must be explicitly granted or configured.
  // This prevents access when a permission key is not recognized or configured.
  return false;
}
